import { ICareMap, IHousehold, IHouseholdMember, IKeyWithMembers, IPatient, ISupportGroup, ISupportGroupMember } from './ICareMap';

const ARRAY_PATIENT_FIELDS: (keyof IPatient)[] = [
    'diagnoses',
    'allergies',
    'medications',
    'contactMethods'
];

const SCALAR_PATIENT_FIELDS: (keyof IPatient)[] = [
    'firstName',
    'lastName',
    'dob',
    'address',
    'photo',
    'photoDataURI',
    'color',
    'avatar',
    'diagnosesIsPublic',
    'allergiesIsPublic',
    'medicationsIsPublic'
];

const KEYS_WITH_MEMBERS: IKeyWithMembers[] = ['community', 'financial', 'school', 'medical', 'specialists', 'social', 'household'];

interface IMergeInput {
    base: ICareMap | undefined;
    local: ICareMap;
    server: ICareMap;
}

interface IMergeInputWithBase {
    base: ICareMap;
    local: ICareMap;
    server: ICareMap;
}

export class CareMapMerger {

    merge(params: IMergeInput): ICareMap {
        const { base, local, server } = params;

        if (!base) {
            return server;
        }

        const mergedPatient = this.mergePatient({ base, local, server });

        const result: ICareMap = {
            ...server,
            patient: mergedPatient
        };

        for (const key of KEYS_WITH_MEMBERS) {
            if (key === 'household') {
                const baseSection = base.household;
                const localSection = local.household;
                const serverSection = server.household;
                const mergedMembers = this.mergeMembers<IHouseholdMember>(
                    { base: baseSection.members, local: localSection.members, server: serverSection.members },
                    ['contactMethods']
                );
                const mergedChecklists = this.mergeAtomicField(
                    { base: baseSection.checklists, local: localSection.checklists, server: serverSection.checklists }
                );
                const merged: IHousehold = {
                    ...serverSection,
                    members: mergedMembers,
                    checklists: mergedChecklists
                };
                result.household = merged;
            } else {
                const baseSection = base[key] as ISupportGroup;
                const localSection = local[key] as ISupportGroup;
                const serverSection = server[key] as ISupportGroup;
                const mergedMembers = this.mergeMembers<ISupportGroupMember>(
                    { base: baseSection.members, local: localSection.members, server: serverSection.members },
                    ['contactMethods']
                );
                const mergedIsRelevant = this.mergeScalarField(
                    { base: baseSection.isRelevant, local: localSection.isRelevant, server: serverSection.isRelevant }
                );
                const mergedIsRelevantSince = this.mergeScalarField(
                    { base: baseSection.isRelevantSince, local: localSection.isRelevantSince, server: serverSection.isRelevantSince }
                );
                const mergedChecklists = this.mergeAtomicField(
                    { base: baseSection.checklists, local: localSection.checklists, server: serverSection.checklists }
                );
                const merged: ISupportGroup = {
                    ...serverSection,
                    members: mergedMembers,
                    isRelevant: mergedIsRelevant,
                    isRelevantSince: mergedIsRelevantSince,
                    checklists: mergedChecklists
                };
                (result as any)[key] = merged;
            }
        }

        const mergedInitialWizardAnswers = this.mergeAtomicField(
            { base: base.initialWizardAnswers, local: local.initialWizardAnswers, server: server.initialWizardAnswers }
        );
        result.initialWizardAnswers = mergedInitialWizardAnswers;

        return result;
    }

    private mergeMembers<T extends { id: string }>(
        { base, local, server }: { base: T[]; local: T[]; server: T[] },
        atomicKeys: string[]
    ): T[] {
        const baseIds = new Set(base.map(m => m.id));
        const serverIds = new Set(server.map(m => m.id));
        const merged: T[] = [];

        // Walk server array in order — preserves database ordering
        for (const serverMember of server) {
            const baseMember = base.find(m => m.id === serverMember.id);
            if (!baseMember) {
                // Server added this member — take as-is
                merged.push(serverMember);
                continue;
            }
            const inLocal = local.find(m => m.id === serverMember.id);
            if (!inLocal) {
                continue; // removed locally
            }
            // Field-level merge — start from server, apply local changes
            merged.push(this.fieldMergeMember(baseMember, inLocal, serverMember, atomicKeys));
        }

        // Members in local but not in base → locally added (skip if server also added same ID — server wins)
        for (const member of local) {
            if (!baseIds.has(member.id) && !serverIds.has(member.id)) {
                merged.push(member);
            }
        }

        return merged;
    }

    private fieldMergeMember<T extends { id: string }>(baseMember: T, inLocal: T, inServer: T, atomicKeys: string[]): T {
        const mergedMember = { ...inServer };
        const serverKeys = Object.keys(inServer);
        const localKeys = Object.keys(inLocal);
        const allKeysSet: { [key: string]: true } = {};
        for (const k of serverKeys) {
            allKeysSet[k] = true;
        }
        for (const k of localKeys) {
            allKeysSet[k] = true;
        }
        const allKeys = Object.keys(allKeysSet);
        for (const key of allKeys as (keyof T)[]) {
            if (key === 'id') {
                continue;
            }
            if (key === 'links') {
                continue; // links always come from server
            }
            if (atomicKeys.includes(key as string)) {
                if (!this.deepEqual(inLocal[key], baseMember[key])) {
                    (mergedMember as any)[key] = inLocal[key];
                }
            } else {
                if (inLocal[key] !== baseMember[key]) {
                    (mergedMember as any)[key] = inLocal[key];
                }
            }
        }
        return mergedMember;
    }

    private mergePatient(params: IMergeInputWithBase): IPatient {
        const merged = this.mergePatientScalars(params);
        return this.mergePatientArrays(params, merged);
    }

    private mergePatientArrays(params: IMergeInputWithBase, merged: IPatient): IPatient {
        const { base, local, server } = params;

        const result: IPatient = { ...merged };

        for (const field of ARRAY_PATIENT_FIELDS) {
            if (!this.deepEqual(local.patient[field], base.patient[field])) {
                (result as any)[field] = local.patient[field];
            } else {
                (result as any)[field] = server.patient[field];
            }
        }

        return result;
    }

    private mergeScalarField<T>(params: { base: T; local: T; server: T }): T {
        const { base, local, server } = params;
        if (local !== base) {
            return local;
        }
        return server;
    }

    private mergeAtomicField<T>(params: { base: T; local: T; server: T }): T {
        const { base, local, server } = params;
        if (!this.deepEqual(local, base)) {
            return local;
        }
        return server;
    }

    private deepEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    private mergePatientScalars(params: IMergeInputWithBase): IPatient {
        const { base, local, server } = params;

        const merged: IPatient = {
            ...server.patient
        };

        for (const field of SCALAR_PATIENT_FIELDS) {
            if (local.patient[field] !== base.patient[field]) {
                (merged as any)[field] = local.patient[field];
            } else {
                (merged as any)[field] = server.patient[field];
            }
        }

        return merged;
    }
}
